import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const PublishingOptions = ({ formData, onFormChange, onPrevious, onPublish }) => {
  const [discountCodes, setDiscountCodes] = useState(formData?.discountCodes || []);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const visibilityOptions = [
    { value: 'public', label: 'Public - Anyone can find and book' },
    { value: 'unlisted', label: 'Unlisted - Only people with link can book' },
    { value: 'private', label: 'Private - Invitation only' }
  ];

  const publishStatusOptions = [
    { value: 'draft', label: 'Save as Draft' },
    { value: 'scheduled', label: 'Schedule for Later' },
    { value: 'published', label: 'Publish Immediately' }
  ];

  const addDiscountCode = () => {
    const newCode = {
      id: Date.now(),
      code: '',
      type: 'percentage',
      value: '',
      maxUses: '',
      validUntil: '',
      description: ''
    };
    const updatedCodes = [...discountCodes, newCode];
    setDiscountCodes(updatedCodes);
    onFormChange({ discountCodes: updatedCodes });
    setShowDiscountForm(true);
  };

  const removeDiscountCode = (codeId) => {
    const updatedCodes = discountCodes?.filter(code => code?.id !== codeId);
    setDiscountCodes(updatedCodes);
    onFormChange({ discountCodes: updatedCodes });
  };

  const updateDiscountCode = (codeId, field, value) => {
    const updatedCodes = discountCodes?.map(code =>
      code?.id === codeId ? { ...code, [field]: value } : code
    );
    setDiscountCodes(updatedCodes);
    onFormChange({ discountCodes: updatedCodes });
  };

  const validateEventData = () => {
    const errors = [];

    // Basic validation
    if (!formData?.title?.trim()) {
      errors?.push('Event title is required');
    }
    
    if (!formData?.category) {
      errors?.push('Event category is required');
    }
    
    if (!formData?.startDate) {
      errors?.push('Event start date is required');
    }
    
    if (!formData?.startTime) {
      errors?.push('Event start time is required');
    }

    // Venue validation
    if (formData?.venueType === 'physical') {
      if (!formData?.venueName?.trim()) {
        errors?.push('Venue name is required for physical events');
      }
      if (!formData?.city?.trim()) {
        errors?.push('City is required for physical events');
      }
    } else if (formData?.venueType === 'virtual') {
      if (!formData?.platform?.trim()) {
        errors?.push('Platform is required for virtual events');
      }
    }

    // Tier validation
    if (!formData?.tiers || formData?.tiers?.length === 0) {
      errors?.push('At least one ticket tier is required');
    } else {
      formData?.tiers?.forEach((tier, index) => {
        if (!tier?.name?.trim()) {
          errors?.push(`Tier ${index + 1}: Name is required`);
        }
        if (!tier?.capacity || parseInt(tier?.capacity) <= 0) {
          errors?.push(`Tier ${index + 1}: Valid capacity is required`);
        }
        if (tier?.price && parseFloat(tier?.price) < 0) {
          errors?.push(`Tier ${index + 1}: Price cannot be negative`);
        }
      });
    }

    // Date validation
    const startDate = new Date(`${formData?.startDate}T${formData?.startTime || '09:00'}`);
    const endDate = formData?.endDate && formData?.endTime ? 
      new Date(`${formData?.endDate}T${formData?.endTime}`) : null;
    
    if (startDate <= new Date()) {
      errors?.push('Event start date must be in the future');
    }
    
    if (endDate && endDate <= startDate) {
      errors?.push('Event end date must be after start date');
    }

    // Schedule publishing validation
    if (formData?.publishStatus === 'scheduled') {
      if (!formData?.publishDate) {
        errors?.push('Publish date is required for scheduled events');
      }
      if (!formData?.publishTime) {
        errors?.push('Publish time is required for scheduled events');
      }
      
      const publishDateTime = new Date(`${formData?.publishDate}T${formData?.publishTime}`);
      if (publishDateTime <= new Date()) {
        errors?.push('Publish date must be in the future');
      }
      if (publishDateTime >= startDate) {
        errors?.push('Publish date must be before event start date');
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    setIsValidating(true);
    setValidationErrors([]);

    // Validate form data
    const errors = validateEventData();
    
    if (errors?.length > 0) {
      setValidationErrors(errors);
      setIsValidating(false);
      return;
    }

    // Proceed with publishing
    setIsValidating(false);
    onPublish();
  };

  return (
    <div className="bg-white border border-border rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Publishing & Promotion</h2>
        <p className="text-muted-foreground">Configure visibility and promotional settings</p>
      </div>
      {/* Validation Errors */}
      {validationErrors?.length > 0 && (
        <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-destructive mb-2">Please fix the following issues:</h4>
              <ul className="text-sm text-destructive/80 space-y-1">
                {validationErrors?.map((error, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="w-1 h-1 bg-destructive/60 rounded-full mt-2 flex-shrink-0"></span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Select
              label="Event Visibility"
              placeholder="Select visibility"
              options={visibilityOptions}
              value={formData?.visibility || 'public'}
              onChange={(value) => onFormChange({ visibility: value })}
              required
              description="Control who can discover your event"
            />

            <Select
              label="Publishing Status"
              placeholder="Select status"
              options={publishStatusOptions}
              value={formData?.publishStatus || 'published'}
              onChange={(value) => onFormChange({ publishStatus: value })}
              required
            />

            {formData?.publishStatus === 'scheduled' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Publish Date"
                  type="date"
                  value={formData?.publishDate || ''}
                  onChange={(e) => onFormChange({ publishDate: e?.target?.value })}
                  required
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                />
                <Input
                  label="Publish Time"
                  type="time"
                  value={formData?.publishTime || ''}
                  onChange={(e) => onFormChange({ publishTime: e?.target?.value })}
                  required
                />
              </div>
            )}

            <div className="space-y-4">
              <label className="block text-sm font-medium text-foreground">
                Registration Settings
              </label>
              <div className="space-y-3">
                <Checkbox
                  label="Require approval for registration"
                  checked={formData?.requireApproval || false}
                  onChange={(e) => onFormChange({ requireApproval: e?.target?.checked })}
                />
                <Checkbox
                  label="Allow group bookings"
                  checked={formData?.allowGroupBookings || false}
                  onChange={(e) => onFormChange({ allowGroupBookings: e?.target?.checked })}
                />
                <Checkbox
                  label="Enable waitlist when sold out"
                  checked={formData?.enableWaitlist || false}
                  onChange={(e) => onFormChange({ enableWaitlist: e?.target?.checked })}
                />
                <Checkbox
                  label="Send confirmation emails"
                  checked={formData?.sendConfirmationEmails !== false}
                  onChange={(e) => onFormChange({ sendConfirmationEmails: e?.target?.checked })}
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-foreground">
                  Discount Codes
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addDiscountCode}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Code
                </Button>
              </div>

              {discountCodes?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="Tag" size={48} className="mx-auto mb-2 opacity-50" />
                  <p>No discount codes created yet</p>
                  <p className="text-xs">Add promotional codes to boost sales</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-64 overflow-y-auto">
                  {discountCodes?.map((code) => (
                    <div key={code?.id} className="border border-border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-foreground">Discount Code</h4>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDiscountCode(code?.id)}
                          className="text-error hover:text-error"
                        >
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input
                          label="Code"
                          type="text"
                          placeholder="e.g., EARLY20"
                          value={code?.code}
                          onChange={(e) => updateDiscountCode(code?.id, 'code', e?.target?.value?.toUpperCase())}
                          required
                        />
                        <div className="flex gap-2">
                          <Input
                            label="Value"
                            type="number"
                            placeholder="20"
                            value={code?.value}
                            onChange={(e) => updateDiscountCode(code?.id, 'value', e?.target?.value)}
                            required
                            min="1"
                          />
                          <select
                            value={code?.type}
                            onChange={(e) => updateDiscountCode(code?.id, 'type', e?.target?.value)}
                            className="mt-6 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            <option value="percentage">%</option>
                            <option value="fixed">$</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input
                          label="Max Uses"
                          type="number"
                          placeholder="100"
                          value={code?.maxUses}
                          onChange={(e) => updateDiscountCode(code?.id, 'maxUses', e?.target?.value)}
                          min="1"
                        />
                        <Input
                          label="Valid Until"
                          type="date"
                          value={code?.validUntil}
                          onChange={(e) => updateDiscountCode(code?.id, 'validUntil', e?.target?.value)}
                        />
                      </div>

                      <Input
                        label="Description"
                        type="text"
                        placeholder="Early bird discount"
                        value={code?.description}
                        onChange={(e) => updateDiscountCode(code?.id, 'description', e?.target?.value)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-accent p-4 rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">Event Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Title:</span>
              <p className="font-medium text-foreground">{formData?.title || 'Not set'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Category:</span>
              <p className="font-medium text-foreground">{formData?.category || 'Not set'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Date:</span>
              <p className="font-medium text-foreground">
                {formData?.startDate ? new Date(formData.startDate)?.toLocaleDateString() : 'Not set'}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Tiers:</span>
              <p className="font-medium text-foreground">
                {formData?.tiers ? formData?.tiers?.length : 0} configured
              </p>
            </div>
          </div>
          {formData?.tiers && formData?.tiers?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total Capacity:</span>
                <span className="font-medium text-foreground">
                  {formData?.tiers?.reduce((total, tier) => total + (parseInt(tier?.capacity) || 0), 0)} tickets
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between pt-6 border-t border-border">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onPrevious}
            disabled={isValidating}
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to Ticketing
          </Button>
          <div className="flex gap-3">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => {
                onFormChange({ publishStatus: 'draft' });
                handleSubmit({ preventDefault: () => {} });
              }}
              disabled={isValidating}
            >
              {isValidating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent mr-2"></div>
              ) : (
                <Icon name="Save" size={16} className="mr-2" />
              )}
              Save Draft
            </Button>
            <Button 
              type="submit" 
              disabled={isValidating}
            >
              {isValidating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              ) : (
                <Icon name="Rocket" size={16} className="mr-2" />
              )}
              {formData?.publishStatus === 'scheduled' ? 'Schedule Event' : 'Publish Event'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PublishingOptions;